import { addDays, format } from 'date-fns';
import type { GuideSurfaceSection } from './guides';

export type PlaybookReminderTemplate = {
  key: string;
  title: string;
  notes: string;
  offsetDays: number;
  section: GuideSurfaceSection;
};

export type PlaybookReminderSeed = {
  sourceKey: string;
  title: string;
  notes: string;
  dueDate: string;
  section: GuideSurfaceSection;
};

function normalise(value: string) {
  return value.toLowerCase();
}

export function derivePlaybookReminderTemplates(playbookTitle: string): PlaybookReminderTemplate[] {
  const title = normalise(playbookTitle);

  if (title.includes('separation') || title.includes('divorce')) {
    return [
      {
        key: 'hmrc-household-change',
        title: 'Notify HMRC about the household change',
        notes: 'Adulting follow-through from the separation guide. Keep the tax and household details updated once arrangements are clearer.',
        offsetDays: 42,
        section: 'money',
      },
      {
        key: 'benefits-rescan',
        title: 'Re-run the benefits scan',
        notes: 'Adulting follow-through from the separation guide. Check support again once the arrangements and income picture settle.',
        offsetDays: 90,
        section: 'money',
      },
    ];
  }

  if (title.includes('redundancy')) {
    return [
      {
        key: 'final-pay-check',
        title: 'Check final pay, notice, and holiday pay',
        notes: 'Adulting follow-through from the redundancy guide. Keep the financial end of the process visible early.',
        offsetDays: 7,
        section: 'work',
      },
      {
        key: 'benefits-check',
        title: 'Check benefits and survival support',
        notes: 'Adulting follow-through from the redundancy guide. Re-check support once income changes become concrete.',
        offsetDays: 14,
        section: 'money',
      },
    ];
  }

  if (title.includes('moving')) {
    return [
      {
        key: 'mail-redirect',
        title: 'Book mail redirect and address updates',
        notes: 'Adulting follow-through from the moving guide. Keep the provider and post move list written down.',
        offsetDays: -14,
        section: 'home',
      },
      {
        key: 'meter-reads',
        title: 'Record meter reads and final utility checks',
        notes: 'Adulting follow-through from the moving guide. Keep evidence from move week in one place.',
        offsetDays: -2,
        section: 'home',
      },
      {
        key: 'post-move-admin',
        title: 'Update council tax, GP, DVLA, and insurers',
        notes: 'Adulting follow-through from the moving guide. Catch the admin that usually slips after the move itself.',
        offsetDays: 7,
        section: 'home',
      },
    ];
  }

  if (title.includes('self-employment')) {
    return [
      {
        key: 'first-invoice',
        title: 'Set up the first invoice and payment trail',
        notes: 'Adulting follow-through from the self-employment guide. Start the work admin loop before money gets fuzzy.',
        offsetDays: 7,
        section: 'work',
      },
      {
        key: 'tax-pot-review',
        title: 'Review tax pot and recurring costs',
        notes: 'Adulting follow-through from the self-employment guide. Check the cashflow picture once the first month settles.',
        offsetDays: 30,
        section: 'work',
      },
    ];
  }

  if (title.includes('school')) {
    return [
      {
        key: 'school-admin-check',
        title: 'Check forms, uniform, meals, and wraparound',
        notes: 'Adulting follow-through from the school-start guide. Keep the practical setup visible before term starts.',
        offsetDays: -14,
        section: 'family',
      },
      {
        key: 'routine-review',
        title: 'Review the first-week routine',
        notes: 'Adulting follow-through from the school-start guide. Catch what needs adjusting before it turns into stress.',
        offsetDays: 7,
        section: 'family',
      },
    ];
  }

  if (title.includes('returning to work') || title.includes('return to work')) {
    return [
      {
        key: 'adjustment-check',
        title: 'Check that the agreed support is actually in place',
        notes: 'Adulting follow-through from the return-to-work guide. Keep the first review visible instead of waiting for it to drift.',
        offsetDays: 7,
        section: 'work',
      },
      {
        key: 'energy-review',
        title: 'Review how the new pattern is affecting energy and caring load',
        notes: 'Adulting follow-through from the return-to-work guide. Re-run Life Check if the new routine is not holding.',
        offsetDays: 21,
        section: 'health',
      },
    ];
  }

  return [];
}

export function buildPlaybookReminderSeeds(playbookTitle: string, anchorDate: string) {
  return derivePlaybookReminderTemplates(playbookTitle).map((template) => ({
    sourceKey: template.key,
    title: template.title,
    notes: template.notes,
    dueDate: format(addDays(new Date(`${anchorDate}T12:00:00`), template.offsetDays), 'yyyy-MM-dd'),
    section: template.section,
  })) satisfies PlaybookReminderSeed[];
}
